<template>
  <div class="research-page">

    <!-- ── Page Header ── -->
    <section class="page-header">
      <h1 class="page-title">Research &amp; Publications</h1>
      <p class="page-subtitle">
        I am a researcher with a focus on human computer interaction, digital product
        design, speech recognition, natural language processing and artificial intelligence.
      </p>
      <div class="stats-row">
        <div class="stat-item">
          <span class="stat-num">15+</span>
          <span class="stat-label">Publications</span>
        </div>
        <div class="stat-item">
          <span class="stat-num">5</span>
          <span class="stat-label">Research Areas</span>
        </div>
        <div class="stat-item">
          <span class="stat-num">6</span>
          <span class="stat-label">Conferences</span>
        </div>
        <div class="stat-item">
          <span class="stat-num">16</span>
          <span class="stat-label">Journal Papers</span>
        </div>
      </div>
    </section>

    <!-- ── Research Focus Areas ── -->
    <section class="focus-section">
      <div class="section-inner">
        <h2 class="section-title">Research Focus Areas</h2>
        <div class="focus-grid">
          <div v-for="area in focusAreas" :key="area.title" class="focus-card">
            <h4 class="focus-card-title">{{ area.title }}</h4>
            <p class="focus-card-desc">{{ area.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Journal Publications ── -->
    <section class="pubs-section">
      <div class="section-inner">
        <h2 class="section-title">Journal Publications</h2>
        <p class="section-desc">
          A collection of publications in <strong>SCI, Scopus, and UGC</strong> approved journals highlighting
          contributions to Digital Product Design, UX Design, Speech Recognition, and more.
        </p>
        <div class="pub-list">
          <div
            v-for="(pub, idx) in journalPublications"
            :key="idx"
            class="pub-item"
          >
            <div class="pub-badges">
              <span class="pub-date-badge">{{ pub.date }}</span>
              <span class="pub-type-badge" :class="'type-' + pub.type.toLowerCase()">{{ pub.type }}</span>
            </div>
            <p class="pub-title">{{ pub.title }}</p>
            <p class="pub-authors">{{ pub.authors.join(', ') }}</p>
            <a v-if="pub.link" :href="pub.link" target="_blank" class="pub-link">View &rarr;</a>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Book Chapters ── -->
    <section class="pubs-section pubs-section--alt">
      <div class="section-inner">
        <h2 class="section-title">Book Chapters</h2>
        <p class="section-desc">
          Scopus indexed book chapters featuring topics like <strong>HCI and IoT-based</strong> solutions.
        </p>
        <div class="pub-list">
          <div
            v-for="(pub, idx) in bookChapters"
            :key="idx"
            class="pub-item"
          >
            <div class="pub-badges">
              <span class="pub-date-badge">{{ pub.date }}</span>
              <span class="pub-type-badge type-scopus">{{ pub.type }}</span>
            </div>
            <p class="pub-title">{{ pub.title }}</p>
            <p class="pub-authors">{{ pub.authors.join(', ') }}</p>
            <a v-if="pub.link" :href="pub.link" target="_blank" class="pub-link">View &rarr;</a>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Conference Papers ── -->
    <section class="pubs-section">
      <div class="section-inner">
        <h2 class="section-title">Conference Papers</h2>
        <p class="section-desc">
          Papers presented at international conferences focusing on <strong>emerging trends in AI, UX Design, and HCI.</strong>
        </p>
        <div class="pub-list">
          <div
            v-for="(pub, idx) in conferencePapers"
            :key="idx"
            class="pub-item"
          >
            <div class="pub-badges">
              <span class="pub-date-badge">{{ pub.date }}</span>
              <span class="pub-type-badge" :class="'type-' + pub.type.toLowerCase()">{{ pub.type }}</span>
            </div>
            <p class="pub-title">{{ pub.title }}</p>
            <p class="pub-authors">{{ pub.authors.join(', ') }}</p>
            <a v-if="pub.link" :href="pub.link" target="_blank" class="pub-link">View &rarr;</a>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Current Research Projects ── -->
    <section class="current-section">
      <div class="section-inner">
        <h2 class="section-title">Current Research Projects</h2>
        <div class="current-grid">
          <div class="current-card">
            <div class="current-card-icon">🤖</div>
            <h4>AI-Assisted Design Tools</h4>
            <p>Investigating how artificial intelligence can augment the UX design process, improve ideation, and reduce cognitive load for designers working on complex products.</p>
          </div>
          <div class="current-card">
            <div class="current-card-icon">🌐</div>
            <h4>Conversational User Interfaces</h4>
            <p>Exploring the design principles and interaction patterns for next-generation conversational interfaces, including voice assistants and chatbots in regional languages.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Research Projects ── -->
    <!-- <section class="projects-section">
      <div class="section-inner">
        <h2 class="section-title">Research Projects</h2>
        <div class="projects-grid">
          <div
            v-for="(project, idx) in researchProjects"
            :key="idx"
            class="project-card"
          >
            <div class="project-card-img-wrap">
              <img :src="project.img" :alt="project.title" class="project-card-img" />
            </div>
            <div class="project-card-body">
              <span class="project-tag">{{ project.category }}</span>
              <h4 class="project-card-title">{{ project.title }}</h4>
              <p class="project-card-desc">{{ project.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section> -->

  </div>
</template>

<script>
const imageMap = require.context('@/assets', false, /\.(png|jpe?g|gif|svg|webp)$/);
function assetUrl(filename) {
  try { return imageMap('./' + filename); } catch { return ''; }
}

export default {
  name: 'ResearchPage',
  data() {
    return {
      focusAreas: [],
      journalPublications: [],
      bookChapters: [],
      conferencePapers: [],
      researchProjects: [],
    };
  },
  async mounted() {
    try {
      const url = process.env.BASE_URL + 'data/research.json';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`);
      const data = await res.json();
      this.focusAreas          = data.focusAreas;
      this.journalPublications = data.journalPublications;
      this.bookChapters        = data.bookChapters;
      this.conferencePapers    = data.conferencePapers;
      this.researchProjects    = data.researchProjects.map(p => ({ ...p, img: assetUrl(p.img) }));
    } catch (err) {
      console.error('research.json load failed:', err);
    }
  },
};
</script>

<style scoped>
/* ── Page ── */
.research-page {
  background: var(--color-bg-white);
  min-height: 100vh;
  overflow-x: hidden;
}

/* ── Header ── */
.page-header {
  text-align: center;
  padding: 7rem var(--space-6) var(--space-16);
  background: var(--color-bg-light);
  border-bottom: 1px solid var(--color-bg-subtle);
}
/* .page-title and .page-subtitle come from global style.css */
.page-subtitle { margin-bottom: var(--space-10); }
.stats-row {
  display: flex;
  justify-content: center;
  gap: var(--space-12);
  flex-wrap: wrap;
}
.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stat-num {
  font-size: var(--text-3xl);
  font-weight: var(--font-extrabold);
  color: var(--color-primary);
  line-height: 1;
}
.stat-label {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  font-weight: var(--font-medium);
  margin-top: 0.3rem;
}

/* ── Shared section layout ── */
.section-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
}
/* .section-title and .section-desc come from global style.css */
.pubs-section--alt { background: var(--color-bg-light); }

/* ── Focus Areas ── */
.focus-section { background: var(--color-bg-light); border-bottom: 1px solid var(--color-bg-subtle); }
.focus-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-5);
}
.focus-card {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.focus-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}
.focus-card-title {
  font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: var(--space-2);
}
.focus-card-desc {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  margin: 0;
}

/* ── Publication list ── */
.pub-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-5);
}
.pub-item {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.pub-item:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}
.pubs-section--alt .pub-item {
  background: var(--color-bg-white);
}
.pub-badges {
  display: flex;
  gap: var(--space-2);
  margin-bottom: 0.6rem;
}
.pub-date-badge {
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  color: var(--color-text-300);
  background: var(--color-bg-subtle);
  border-radius: var(--radius-full);
  padding: 0.2rem 0.65rem;
}
.pub-type-badge {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  border-radius: var(--radius-full);
  padding: 0.2rem 0.65rem;
}
.type-sci { background: var(--color-blue-50); color: var(--color-blue-700); }
.type-scopus { background: var(--color-green-50); color: var(--color-green-600); }
.type-ugc { background: var(--color-primary-subtle); color: var(--color-primary-dark); }
.type-copyright { background: var(--color-purple-50); color: var(--color-purple-600); }
.type-design { background: var(--color-pink-50); color: var(--color-pink-600); }

.pub-title {
  /* font-size: var(--text-sm);
  font-weight: var(--font-semibold); */
   font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  line-height: var(--leading-normal);
  margin: 0 0 0.35rem;
}
.pub-authors {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  margin: 0 0 0.6rem;
  flex: 1;
}
.pub-link {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  text-decoration: none;
}
.pub-link:hover { color: var(--color-primary-dark); }

/* ── Current Research ── */
.current-section { background: var(--color-primary-subtle); }
.current-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-6);
}
.current-card {
  background: var(--color-bg-white);
  border: 1px solid var(--color-primary-border);
  border-radius: var(--radius-xl);
  padding: var(--space-8);
}
.current-card-icon { font-size: var(--text-2xl); margin-bottom: var(--space-4); }
.current-card h4 {
  font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: 0.6rem;
}
.current-card p {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  margin: 0;
}

/* ── Research Projects ── */
.projects-section { background: var(--color-bg-light); }
.projects-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-6);
}
.project-card {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  overflow: hidden;
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.project-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-3px);
}
.project-card-img-wrap {
  height: 180px;
  overflow: hidden;
  background: var(--color-bg-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
}
.project-card-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform var(--duration-slow) var(--ease-out);
}
.project-card:hover .project-card-img { transform: scale(1.04); }
.project-card-body { padding: var(--space-5); }
.project-tag {
  display: inline-block;
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  background: var(--color-primary-subtle);
  border-radius: var(--radius-full);
  padding: 0.2rem 0.65rem;
  margin-bottom: var(--space-2);
}
.project-card-title {
  font-size: var(--text-sm);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin: 0 0 0.4rem;
}
.project-card-desc {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .focus-grid { grid-template-columns: repeat(2, 1fr); }
  .current-grid { grid-template-columns: 1fr; }
  .projects-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .page-header { padding: 6rem var(--space-4) var(--space-12); }
  .stats-row { gap: var(--space-6); }
  .focus-grid { grid-template-columns: 1fr; }
  .pub-list { grid-template-columns: 1fr; }
  .projects-grid { grid-template-columns: 1fr; }
  .section-inner { padding: var(--space-12) var(--space-4); }
}
</style>
